// CD2 — attachment byte custody. Composer image BYTES live here as Blobs
// (IndexedDB when the page has it, an in-memory map otherwise) while the
// synchronous composer store persists only opaque references
// (id/name/mimeType/sizeBytes). Before CD2, every keystroke's debounced
// persist re-serialized megabytes of base64 through localStorage.
//
// Custody laws:
//   - custody never throws into the composer store: put reports boolean,
//     get degrades to null (a lost blob is a typed absent attachment);
//   - legacy dataUrl bytes captured at decode ride a localStorage sidecar
//     until their blob write SETTLES, so an interrupted backfill loses
//     nothing — the main store may rewrite refs-only immediately;
//   - the in-memory fallback is non-durable by design (a degraded context
//     keeps working for the session; reload loses only previews, never the
//     live draft text).

export interface AttachmentBlobBackend {
  put(id: string, blob: Blob): Promise<void>;
  get(id: string): Promise<Blob | null>;
  delete(ids: ReadonlyArray<string>): Promise<void>;
}

const DB_NAME = "cleo-composer-attachments";
const DB_STORE = "blobs";

function createMemoryBlobBackend(): AttachmentBlobBackend {
  const blobs = new Map<string, Blob>();
  return {
    put: async (id, blob) => {
      blobs.set(id, blob);
    },
    get: async (id) => blobs.get(id) ?? null,
    delete: async (ids) => {
      for (const id of ids) blobs.delete(id);
    },
  };
}

function createIndexedDbBlobBackend(factory: IDBFactory): AttachmentBlobBackend {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const openDb = (): Promise<IDBDatabase> => {
    dbPromise ??= new Promise((resolve, reject) => {
      const request = factory.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(DB_STORE)) {
          request.result.createObjectStore(DB_STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        dbPromise = null;
        reject(request.error ?? new Error("indexedDB open failed"));
      };
    });
    return dbPromise;
  };
  const inTransaction = <T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> =>
    openDb().then(
      (db) =>
        new Promise<T>((resolve, reject) => {
          const transaction = db.transaction(DB_STORE, mode);
          const request = run(transaction.objectStore(DB_STORE));
          transaction.oncomplete = () => resolve(request.result);
          transaction.onerror = () => reject(transaction.error ?? new Error("transaction failed"));
          transaction.onabort = () => reject(transaction.error ?? new Error("transaction aborted"));
        }),
    );
  return {
    put: (id, blob) => inTransaction("readwrite", (store) => store.put(blob, id)).then(() => {}),
    get: (id) =>
      inTransaction<unknown>("readonly", (store) => store.get(id)).then((value) =>
        value instanceof Blob ? value : null,
      ),
    delete: (ids) =>
      ids.length === 0
        ? Promise.resolve()
        : openDb().then(
            (db) =>
              new Promise<void>((resolve, reject) => {
                const transaction = db.transaction(DB_STORE, "readwrite");
                const store = transaction.objectStore(DB_STORE);
                for (const id of ids) store.delete(id);
                transaction.oncomplete = () => resolve();
                transaction.onerror = () => reject(transaction.error ?? new Error("delete failed"));
              }),
          ),
  };
}

let testBackend: AttachmentBlobBackend | null = null;
let defaultBackend: AttachmentBlobBackend | null = null;

function resolveBackend(): AttachmentBlobBackend {
  if (testBackend !== null) return testBackend;
  defaultBackend ??=
    typeof indexedDB !== "undefined"
      ? createIndexedDbBlobBackend(indexedDB)
      : createMemoryBlobBackend();
  return defaultBackend;
}

/** Test seam. Passing null restores default backend resolution. */
export function setAttachmentBlobBackendForTests(backend: AttachmentBlobBackend | null): void {
  testBackend = backend;
}

// Writes still in flight. Saving drafts before a Desk update waits for these,
// so an image's reference can join its draft before the window is destroyed.
const pendingPuts = new Set<Promise<boolean>>();

export function putAttachmentBlob(id: string, blob: Blob): Promise<boolean> {
  const put = (async () => {
    try {
      await resolveBackend().put(id, blob);
      return true;
    } catch {
      return false;
    }
  })();
  pendingPuts.add(put);
  void put.then(() => pendingPuts.delete(put));
  return put;
}

/** Resolves once every attachment write started so far has settled. */
export async function settlePendingAttachmentBlobWrites(): Promise<void> {
  await Promise.all(pendingPuts);
}

export async function getAttachmentBlob(id: string): Promise<Blob | null> {
  try {
    return await resolveBackend().get(id);
  } catch {
    return null;
  }
}

export async function deleteAttachmentBlobs(ids: ReadonlyArray<string>): Promise<void> {
  try {
    await resolveBackend().delete(ids);
  } catch {
    // Deletion is garbage collection; a failure only leaves an orphan blob.
  }
}

export function dataUrlToBlob(dataUrl: string): Blob | null {
  const commaIndex = dataUrl.indexOf(",");
  if (commaIndex === -1) return null;
  const header = dataUrl.slice(0, commaIndex);
  const payload = dataUrl.slice(commaIndex + 1);
  if (payload.length === 0 || !header.startsWith("data:")) return null;
  const mimeType = header.slice("data:".length).split(";")[0] || "application/octet-stream";
  try {
    if (header.includes(";base64")) {
      const decoded = atob(payload);
      const bytes = new Uint8Array(decoded.length);
      for (let index = 0; index < decoded.length; index += 1) {
        bytes[index] = decoded.charCodeAt(index);
      }
      return new Blob([bytes], { type: mimeType });
    }
    return new Blob([decodeURIComponent(payload)], { type: mimeType });
  } catch {
    return null;
  }
}

// ── Legacy backfill sidecar ────────────────────────────────────────────
//
// Decode of a pre-v9 payload finds attachment bytes inline as data URLs.
// They are stashed HERE (synchronously, same localStorage they already
// occupied) so the main store can rewrite refs-only immediately; the drain
// moves each into the blob backend and removes it only after the write
// settles.

export const ATTACHMENT_BACKFILL_SIDECAR_KEY = "cleo:composer-attachment-backfill:v1";

export interface LegacyAttachmentBytes {
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly dataUrl: string;
}

type SidecarRecord = Record<string, LegacyAttachmentBytes>;

function sidecarStorage(): Pick<Storage, "getItem" | "setItem" | "removeItem"> | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readSidecar(): SidecarRecord {
  const storage = sidecarStorage();
  if (storage === null) return memorySidecar;
  try {
    const raw = storage.getItem(ATTACHMENT_BACKFILL_SIDECAR_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as SidecarRecord) : {};
  } catch {
    return {};
  }
}

function writeSidecar(record: SidecarRecord): void {
  const storage = sidecarStorage();
  if (storage === null) {
    memorySidecar = record;
    return;
  }
  try {
    if (Object.keys(record).length === 0) {
      storage.removeItem(ATTACHMENT_BACKFILL_SIDECAR_KEY);
    } else {
      storage.setItem(ATTACHMENT_BACKFILL_SIDECAR_KEY, JSON.stringify(record));
    }
  } catch {
    // A full/blocked storage keeps the previous sidecar; bytes are not lost,
    // the drain simply retries from the older record next boot.
  }
}

/** No-localStorage contexts (plain node tests) keep the sidecar in memory. */
let memorySidecar: SidecarRecord = {};

/** Synchronous capture at decode time: legacy inline bytes enter custody. */
export function stashLegacyAttachmentBytes(id: string, bytes: LegacyAttachmentBytes): void {
  const record = readSidecar();
  record[id] = bytes;
  writeSidecar(record);
}

export function readLegacyAttachmentBytes(): SidecarRecord {
  return readSidecar();
}

/**
 * Move every sidecar entry into the blob backend. An entry leaves the
 * sidecar only after its blob write settles; a failed conversion or write
 * keeps it for the next drain.
 */
export async function drainAttachmentBackfillSidecar(): Promise<void> {
  const record = readSidecar();
  const ids = Object.keys(record);
  if (ids.length === 0) return;
  for (const id of ids) {
    const entry = record[id];
    if (entry === undefined) continue;
    const blob = entry.dataUrl ? dataUrlToBlob(entry.dataUrl) : null;
    if (blob === null) {
      // Unreadable bytes can never become a blob; dropping the entry is the
      // typed absence, not a loss the drain could ever repair.
      delete record[id];
      continue;
    }
    const settled = await putAttachmentBlob(id, blob);
    if (settled) {
      delete record[id];
    }
  }
  writeSidecar(record);
}
